/**
 * Saha listesi — yapılandırmanın giriş kapısı [K-001: saha birincil varlık].
 *
 * Kart düzeni, tablo değil: mobil öncelikli [K-020]. 320 px genişlikte bir
 * tablo ya yatay kaydırılır (saha ekibi tek eliyle kullanacak) ya da sütunları
 * kırpılır. Kart her satırı kendi içinde okunur kılar.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

import {
  AramaKutusu,
  EklemeBaglantisi,
  FiltreDugmesi,
  FiltreSatiri,
  KapsamNotu,
  ListeDurumu,
  YapilandirmaSekmeleri,
  useAramaYazici,
} from './parts.tsx';
import { Badge } from '../design/Status.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { sahaDurumu } from './durum.ts';
import { useMusteriler, useSahalar, type SahaSatiri } from './api.ts';

export function SahaListesi(): React.JSX.Element {
  const { q, musteri } = useSearch({ from: '/korumali/sahalar' });
  const navigate = useNavigate();

  const git = useCallback(
    (yeni: { q?: string | undefined; musteri?: number | undefined }) => {
      void navigate({
        to: '/sahalar',
        search: {
          ...(yeni.q === undefined ? {} : { q: yeni.q }),
          ...(yeni.musteri === undefined ? {} : { musteri: yeni.musteri }),
        },
        // `replace`: her arama tarayıcı geçmişine girmez.
        replace: true,
      });
    },
    [navigate],
  );
  const aramaDegisti = useAramaYazici(
    useCallback((deger: string | undefined) => git({ q: deger, musteri }), [git, musteri]),
  );
  const temizle = useCallback(() => git({}), [git]);

  /*
   * Filtre ETKİNKEN adı yazılıyor. "Limak Çimento sahaları" demek, boş bir
   * listeyi gören kullanıcının "sahalarım nerede?" diye paniklemesini önler.
   * Müşteri adı için izin yoksa filtre yine görünüyor, adı olmadan.
   */
  const musteriler = useMusteriler();
  const musteriAdi =
    musteri === undefined
      ? null
      : (musteriler.data?.find((m) => m.id === musteri)?.title ?? 'Seçili müşteri');

  const sorgu = useSahalar({ q, musteri });

  return (
    <>
      <PageHeader
        title="Sahalar"
        description="Yapılandırılmış sahalar ve izleme durumları"
        action={
          <EklemeBaglantisi gerekli="site_create" to="/sahalar/yeni" etiket="Saha ekle" vurgulu />
        }
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />
      <AramaKutusu
        deger={q ?? ''}
        degisti={aramaDegisti}
        ipucu="Saha adı, tesisat kodu veya şehir"
      />

      {musteriAdi !== null && (
        <FiltreSatiri>
          <FiltreDugmesi etkin onClick={() => git({ q })}>
            {musteriAdi} sahaları ✕
          </FiltreDugmesi>
        </FiltreSatiri>
      )}

      <PageBody>
        <ListeDurumu
          sorgu={sorgu}
          filtreVar={q !== undefined || musteri !== undefined}
          filtreyiTemizle={temizle}
          bosBaslik="Henüz saha yok"
          bosAciklama="Sahalar buraya elle girilecek [K-011]; eski sistemden veri taşınmıyor."
          bosEylem={
            <EklemeBaglantisi
              gerekli="site_create"
              to="/sahalar/yeni"
              etiket="İlk sahayı ekle"
              vurgulu
            />
          }
        >
          {(sahalar) => (
            <ul className="flex flex-col gap-3">
              {sahalar.map((saha) => (
                <li key={saha.id}>
                  <SahaKarti saha={saha} />
                </li>
              ))}
            </ul>
          )}
        </ListeDurumu>
      </PageBody>
    </>
  );
}

function SahaKarti({ saha }: { readonly saha: SahaSatiri }): React.JSX.Element {
  const durum = sahaDurumu(saha);
  const yer = [saha.district, saha.city].filter((v) => v !== null && v !== '').join(' / ');

  return (
    <Card>
      <CardBody>
        {/*
         * Bağlantı KARTIN TAMAMI değil, başlığı: kartın içinde ileride başka
         * bağlantılar olacak (pano, tank) ve iç içe bağlantı erişilebilirlik
         * açısından geçersizdir.
         */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to="/sahalar/$sahaId"
              params={{ sahaId: String(saha.id) }}
              className="text-base font-semibold text-ink hover:text-brand-700"
            >
              {saha.name}
            </Link>
            <p className="mt-0.5 truncate text-sm text-ink-muted">
              {saha.addr_code}
              {yer !== '' && ` · ${yer}`}
            </p>
          </div>
          <Badge tone={durum.tone}>{durum.metin}</Badge>
        </div>

        <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted">
          <div className="flex gap-1">
            <dt>Pano:</dt>
            <dd className="font-medium text-ink">{saha.panel_count}</dd>
          </div>
          <div className="flex gap-1">
            <dt>Tank:</dt>
            <dd className="font-medium text-ink">{saha.tank_count}</dd>
          </div>
          {/*
           * Müşterisiz saha MEŞRUDUR [K-001] — "—" göstermek yerine hiç
           * göstermiyoruz; eksik gibi durmasın.
           */}
          {saha.customer_title !== null && (
            <div className="flex min-w-0 gap-1">
              <dt>Müşteri:</dt>
              <dd className="truncate font-medium text-ink">{saha.customer_title}</dd>
            </div>
          )}
        </dl>
      </CardBody>
    </Card>
  );
}
